import React, {useCallback, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Linking, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';

import Button from '../../components/themed/Button';
import Dialog from '../../components/ui-controls/Dialog';
import FormView from '../../components/ui-controls/form/FormView';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {SelfExclusionFormData, selfExclusionSchema} from './schemas/selfExclusionSchema';

function InputLabel({children, required}: {children: React.ReactNode; required?: boolean}) {
  return (
    <Text style={styles.label}>
      {children}
      {!!required && (
        <Text style={styles.requiredAsterisk} accessibilityLabel="required field">
          {' *'}
        </Text>
      )}
    </Text>
  );
}

const SuccessDialog = ({isVisible, onOk, submittedTicketNumber}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Your request was successfully submitted. Thank you.">
    <View>
      <Text style={styles.dialogText}>Your ticket number is #{submittedTicketNumber}</Text>
    </View>
  </Dialog>
);

const ErrorDialog = ({isVisible, onOk}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Sorry, we couldn't submit your ticket at this moment. Please try again later."
  />
);

const SelfExclusionScreen = ({navigation}: RouteNavigatorScreenProps<AppRoute.SELF_EXCLUSION>) => {
  const {userStore} = useStores();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setErrorDialogVisible] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  const defaultValues: SelfExclusionFormData = {
    firstName: userStore.firstName,
    lastName: userStore.lastName,
    email: userStore.emailAddress,
    phone: userStore.phoneNumber,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, touchedFields},
  } = useForm<SelfExclusionFormData>({
    resolver: zodResolver(selfExclusionSchema),
    defaultValues,
  });

  // Reset form on navigation blur (screen pattern - NOT useEffect)
  useFocusEffect(
    useCallback(() => {
      return () => {
        // Reset form on navigation blur
        reset();
      };
    }, [reset]),
  );

  const handleSuccessDialogOk = () => {
    setSuccessDialogVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  const handleErrorDialogOk = () => {
    setErrorDialogVisible(false);
  };

  const onSubmit = async (values: SelfExclusionFormData) => {
    try {
      setSubmitting(true);
      const response = await apiService.createSelfExclusionRequest(values);
      const {ticketNumber} = response;
      if (!ticketNumber) {
        logger.error(
          'SelfExclusionScreen',
          'Support Ticket was submitted successfully but no ticket number was received',
          {response},
        );
        throw new Error();
      }
      setSubmitting(false);
      setSubmittedTicketNumber(ticketNumber);
      setSuccessDialogVisible(true);
    } catch (_err) {
      setSubmitting(false);
      setErrorDialogVisible(true);
    }
  };

  const validationMessage = (field: keyof SelfExclusionFormData) => {
    if (errors[field] && touchedFields[field]) {
      return <ValidationMessage>{errors[field]?.message}</ValidationMessage>;
    }
    return null;
  };

  // The Android manifest is adjustPan, which never resizes the window, so the
  // keyboard inset has to be applied by the scroll view itself. DIS-1551.
  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      keyboardShouldPersistTaps="always"
      enableOnAndroid
      extraScrollHeight={40}>
      <ContentView testID="self-exclusion-screen">
        <Text style={styles.headline}>Exclude yourself from our services</Text>
        <Text style={styles.copy}>
          We care about the safety of our customers and aim to promote the responsible consumption
          of alcohol. If you would like to exclude yourself from {config.APP_NAME}&apos;s online
          services, you can fill in the below form with your details. By submitting the form, you
          will no longer be able to place an order through our website. Your information will be
          handled in line with our privacy policy.{'\n'}
          {'\n'}If you or someone you know may have a problem with alcohol, you can find information
          on support services{' '}
          <Text
            style={styles.textLink}
            onPress={() =>
              Linking.openURL(
                'https://drinkwiseaustr.wpengine.com/drinking-and-you/support-services/#',
              )
            }>
            here
          </Text>
          .
        </Text>
        <Text style={styles.requiredIndicator}>* indicates required field</Text>
        <FormView>
          <View>
            <View style={styles.fieldContainer}>
              <InputLabel required>First name</InputLabel>
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-given"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="First name"
                    renderErrorMessage={false}
                    touched={touchedFields.firstName}
                  />
                )}
              />
              {validationMessage('firstName')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Last name</InputLabel>
              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-family"
                    value={value}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Last name"
                    renderErrorMessage={false}
                    touched={touchedFields.lastName}
                  />
                )}
              />
              {validationMessage('lastName')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Email</InputLabel>
              <Controller
                control={control}
                name="email"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="email"
                    value={value}
                    keyboardType="email-address"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Email"
                    renderErrorMessage={false}
                    touched={touchedFields.email}
                  />
                )}
              />
              {validationMessage('email')}
            </View>
            <View style={styles.fieldContainer}>
              <InputLabel required>Phone number</InputLabel>
              <Controller
                control={control}
                name="phone"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="tel"
                    value={value}
                    keyboardType="number-pad"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Phone number"
                    renderErrorMessage={false}
                    touched={touchedFields.phone}
                  />
                )}
              />
              {validationMessage('phone')}
            </View>
          </View>
          <Button
            onPress={handleSubmit(onSubmit)}
            title="Submit request"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
          <SuccessDialog
            isVisible={isSuccessDialogVisible}
            onOk={handleSuccessDialogOk}
            submittedTicketNumber={submittedTicketNumber}
          />
          <ErrorDialog isVisible={isErrorDialogVisible} onOk={handleErrorDialogOk} />
        </FormView>
      </ContentView>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  dialogText: {
    fontSize: 16,
    marginBottom: 8,
    marginTop: 8,
  },
  textLink: {
    color: theme.colour.text.link.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  label: {
    fontSize: 16,
    marginBottom: 5,
  },
  requiredAsterisk: {
    fontSize: 16,
    color: '#ef4444',
    marginLeft: 2,
  },
  requiredIndicator: {
    fontSize: 13,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 8,
    marginBottom: 8,
  },
  fieldContainer: {
    marginBottom: 20,
  },
});

export default SelfExclusionScreen;
